export type Lang = 'en' | 'ja';

export type StringTable = Record<string, string>;

let jaTable: StringTable | null = null;
const jaListeners = new Set<() => void>();

export function registerJa(table: StringTable) {
  jaTable = table;
  jaListeners.forEach((listener) => listener());
}

export function peekJa() {
  return jaTable;
}

export function subscribeJa(onStoreChange: () => void) {
  jaListeners.add(onStoreChange);
  return () => {
    jaListeners.delete(onStoreChange);
  };
}

export const strings: { en: StringTable } = {
  en: {
    // Header
    'header.subtitle': 'NOISE COMPLAINT FILED // NOISE COMPLAINT DENIED',
    'header.overheardLabel': 'Overheard in the field',
    'header.overheard.1': 'Came for the riffs // stayed for the radiation',
    'header.overheard.2': 'The incinerator has not responded.',
    'header.clearance': 'CLEARANCE',
    'header.login': 'LOGIN',
    'header.logout': 'LOGOUT',
    'header.redacted': 'REDACTED',
    'header.exposed': 'EXPOSED',
    'header.apocalypse': 'T-MINUS TO NEXT SCHEDULED INCIDENT',
    // Nav
    'nav.bunker.code': 'BUNKER',
    'nav.signal.code': 'SIGNAL',
    'nav.ops.code': 'OPS',
    'nav.supply.code': 'SUPPLY',
    'nav.intel.code': 'INTEL',
    'nav.channel.code': 'CHANNEL',
    'nav.menu': 'MENU',
    'nav.close': 'CLOSE',
    // Shared field labels
    'field.catalog': 'CATALOG',
    'field.artist': 'ARTIST',
    'field.title': 'TITLE',
    'field.format': 'FORMAT',
    'field.year': 'YEAR',
    'field.status': 'STATUS',
    'field.releaseDate': 'RELEASE DATE',
    'field.limitedRun': 'LIMITED RUN',
    'field.tags': 'TAGS',
    'field.classification': 'CLASSIFICATION',
    'field.region': 'REGION',
    'field.file': 'FILE',
    // Actions
    'action.buycd': 'Buy CD',
    'action.acquireUnit': 'ACQUIRE UNIT',
    'action.buycdselvajaria': 'BUY CD VIA SELVAJARIA',
    'action.bandcamp': 'VISIT BANDCAMP',
    'band.bandcamp.link': 'VISIT BANDCAMP',
    'action.listenBandcamp': 'Listen on Bandcamp',
    'action.intercept': 'INTERCEPT TRANSMISSION',
    'action.readDossier': 'READ DOSSIER',
    'action.openFile': 'OPEN FILE',
    'action.previousPage': '← Previous page',
    'action.releaseFile': 'Release file',
    'action.viewFootage': 'VIEW FOOTAGE',
    'action.watch': 'Watch',
    'action.openRelease': 'OPEN RELEASE FILE',
    'action.openOperative': 'OPEN OPERATIVE FILE',
    'action.acquire': 'ACQUIRE [CONTACT REQUIRED]',
    'action.signalVault': 'SIGNAL VAULT',
    'action.viewOps': 'VIEW OPS',
    'action.watchAll': 'WATCH ALL VIDEOS',
    // Releases page
    'page.releases.heading': 'SIGNAL',
    'page.releases.sub': 'COMPLETE CATALOG OF SYNDICATE OPERATIONS',
    // Roster page
    'page.roster.heading': 'OPERATIVES',
    'page.roster.sub': 'CURRENT OPERATIONS AND AFFILIATED UNITS',
    'roster.openFile': 'OPEN DOSSIER',
    'roster.openDossierPlain': 'Operative file',
    // Merch page
    'page.merch.heading': 'SUPPLY',
    'page.merch.sub': 'AUTHORIZED PROPAGANDA',
    // Contact page
    'page.contact.heading': 'CHANNEL',
    'page.contact.sub': 'COMMUNICATION CHANNELS',
    // Intel page
    'page.intel.heading': 'INTEL',
    'page.intel.sub': 'N.A.I.F.A. CHARTER AND INTERNAL DOCTRINE',
    // Home
    'home.hero.kicker': 'NSR // INDEPENDENT OPERATION',
    'home.hero.lead':
      'N.A.I.F.A. is the designated authority for sonic incidents, unauthorized frequencies and cacophonous hostilities exceeding the established civilian death threshold.',
    'home.authorizedPropaganda': 'AUTHORIZED PROPAGANDA',
    'home.latestFeedback': '[LATEST FEEDBACK]',
    'home.albumTracklist': 'Album Tracklist',
    'home.assessment': 'Assessment',
    'home.naifaSub': 'Neurotic Atomic Institute of Foreign Affairs',
    'home.naifaEstablished': 'Established: Undisclosed',
    'home.naifaJurisdiction': 'Jurisdiction: Extraterritorial',
    'home.naifaStatus': 'Status: Operational',
    'home.officialDesc': 'Official Description',
    'home.coreDoctrine': 'Core Doctrine',
    'home.structure': 'Structure',
    'home.positionStatement': 'Position Statement',
    'home.latestTransmissions': 'LATEST TRANSMISSIONS',
    'home.latestTransmissionsSub': 'New releases and scheduled interventions',
    'home.latestRelease': 'LATEST RELEASE',
    'home.incomingTransmission': 'INCOMING TRANSMISSION',
    'home.fieldPropaganda': 'FIELD PROPAGANDA',
    'home.payload': 'LATEST PAYLOAD',
    'home.detonationSequence': 'Detonation Sequence',
    'home.burnTime': 'Burn time',
    'home.duration': 'Duration of exposure: 20:44',
    'home.printRun': 'Limited to 200 units.',
    'home.bundle': 'A5 tour-program fanzine, 16 pages, and sticker. Includes access to classified intel.',
    'home.assessmentLead': 'Eleven detonations. Twenty minutes, forty-four seconds of total exposure.',
    'home.assessmentBody':
      'D-beat radiation punk and metallic crust at first-strike velocity, with synths decaying in the background like a monitoring station losing power. Fidelity was not a priority. Safety was not consulted.',
    'home.naifaTeaser':
      'We monitor, contain, and redistribute volatile audio from politically irrelevant and psychologically unstable zones. Nightmare Syndicate Records was established as the agency’s discographic arm. It was meant to be a cover. It is now the entire operation.',
    'home.naifaMeta': 'Jurisdiction: extraterritorial. Funding: undisclosed. Staff: reduced.',
    'home.openCharter': 'OPEN CHARTER',
    'home.openCharterPlain': 'Read the charter',
    'home.nsr02Title': 'FATAL EXPOSURE: The Human Crust Of War',
    'home.nsr02Credit': '(Nightmare Syndicate Records, 2026)',
    'home.nsr02Lead':
      'Finally, the soundtrack to your post-apocalyptic daydreams. Fatal Exposure returns with The Human Crust Of War, a 12-track nuclear tango that answers the question no one asked: What if the Cold War had synths and dbeat?',
    'home.nsr02Body':
      'Expect all your favorite themes: radiation poisoning, military delusion, and flesh-melting regret. From Plutonium Crisis to Neutron Holocaust, this album doesn’t build up to the end. It starts there and just keeps digging.',
    'home.nsr02Close': 'No hope. Just pure fallout.',
    'home.nsr02Quote': '"Perfect for dinner parties, bunker rituals, or watching the sky catch fire."',
    'home.nsr02QuoteBy': '— The Sunday Armistice',
    'home.footage': 'INTERCEPTED FOOTAGE',
    'clearance.action': 'REQUEST ADVANCE CLEARANCE',
    'clearance.plain': 'Email for the next transmission',
    'clearance.email': 'Email',
    'clearance.emailPlaceholder': 'add your email',
    'clearance.success': 'Clearance request logged. Response is not guaranteed.',
    'clearance.error': 'Request refused. Check the address and file again.',
    // Release detail
    'release.backLink': '← BACK TO SIGNALS',
    'release.notFound': 'FILE NOT FOUND',
    'release.returnCatalog': 'RETURN TO CATALOG',
    'release.cdImages': 'CD IMAGE FILES',
    'release.videoFiles': 'VIDEO FILES',
    'release.description': 'Description',
    'release.tracklist': 'Detonation Sequence',
    'release.credits': 'Credits',
    'release.assessment': 'Assessment',
    'release.coverSpeculative': 'cover artwork is purely speculative',
    // Band dossier
    'band.backLink': '← BACK TO ROSTER',
    'band.notFound': 'DOSSIER NOT FOUND',
    'band.returnRoster': 'RETURN TO ROSTER',
    'band.officialWeb': 'OFFICIAL WEB',
    'band.officialEmail': 'OFFICIAL EMAIL',
    'band.summary': 'SUMMARY',
    'band.lineup': 'LINEUP',
    'band.col.operative': 'Operative',
    'band.col.duty': 'Duty',
    'band.notes': 'DOSSIER NOTES',
    'band.visualFiles': 'VISUAL FILES',
    'band.latestRelease': 'LATEST RELEASE',
    'band.contact': 'Contact:',
    // Contact page
    'contact.generalInquiries': 'GENERAL INQUIRIES',
    'contact.aboutOperating': 'ABOUT / OPERATING CONDITIONS',
    'contact.securityNotice': '[SECURITY NOTICE]',
    'contact.doNotMeet': 'DO NOT REQUEST PERSONAL MEETINGS.',
    'contact.finalNote': 'NIGHTMARE SYNDICATE RECORDS operates independently with no affiliations. All communication is conducted through encrypted channels. Response is not guaranteed. Patience required.',
    'contact.emailLabel': 'EMAIL',
    'contact.responseTimeLabel': 'RESPONSE TIME',
    'contact.tradesLabel': 'TRADES',
    'contact.wholesaleLabel': 'WHOLESALE',
    'contact.emailDesc': 'info@nightmaresyndicaterecords.com',
    'contact.responseDesc': '7-14 days. Non-urgent inquiries only. We might not reply.',
    'contact.tradesDesc': 'Contact for trades.',
    'contact.wholesaleDesc': 'Contact for wholesale inquiries.',
    'contact.opCondTitle': 'Operating Conditions',
    'contact.mandateTitle': 'Mandate',
    'contact.mandateIntro': 'Amended 14 times. Ratification status disputed.',
    'contact.mandate.1': 'Identify the source of the noise.',
    'contact.mandate.2': 'Establish whether the source is hostile.',
    'contact.mandate.3': 'If the source is not hostile, revise its classification.',
    'contact.mandate.4': 'If the source cannot be located, designate the entire area hostile.',
    'contact.mandate.5': 'If the area is already hostile, proceed with distribution.',
    'contact.mandate.6': 'If distribution has commenced, deny prior knowledge.',
    'contact.mandate.amendment':
      'The second amendment authorized listening. The third abolished the distinction between listening and firing.',
    'contact.mandate.classified': 'All subsequent amendments are classified as musical material.',
    'contact.distribTitle': 'Distribution Model',
    'contact.disclaimerTitle': 'Disclaimer',
    // Dossiers page
    'dossiers.omegaNode': 'OMEGA NODE',
    'dossiers.heading': 'INTEL INTERFACE',
    'dossiers.clearanceGranted': 'CLEARANCE GRANTED',
    'dossiers.encryptionActive': 'Encryption Active. Login Required.',
    'dossiers.authenticate': 'AUTHENTICATE CREDENTIALS',
    'dossiers.tab.overview': 'OVERVIEW',
    'dossiers.tab.logs': 'LOGS',
    'dossiers.tab.files': 'FILES',
    'dossiers.tab.archive': 'ARCHIVE',
    'dossiers.personnelRoster': 'Personnel Roster',
    'dossiers.adminDocs': 'Administrative Docs',
    'dossiers.transmissionContext': 'TRANSMISSION CONTEXT',
    'dossiers.backgroundIntel': 'BACKGROUND INTELLIGENCE',
    'dossiers.operativeFile': 'OPERATIVE FILE',
    'dossiers.activeDirective': 'ACTIVE DIRECTIVE',
    'dossiers.eventLog': 'Event Log',
    'dossiers.securityContext': 'Security Context',
    'dossiers.unitDesignation': 'Unit Designation',
    'dossiers.noImages': 'No images',
    'dossiers.activeOperative': 'ACTIVE OPERATIVE',
    // Footer
    'footer.rights': 'All rights reserved',
    'footer.disclaimer':
      'Nightmare Syndicate Records is not a government agency. This statement is legally required and factually disputed.',
    'footer.redactedTerm': 'STAFFING ANNEX',
    'footer.login': 'Operator Login',
    'footer.logout': 'Operator Logout',
    'footer.deployedBy': 'Deployed by ISW',
  },
};
